/**
 * Carries tracking state across the two places React state cannot survive:
 *
 *  1. The payment gateway redirect. HyperPay navigates the whole page out to
 *     oppwa.com and back to /sim/order/create (or /order/create for eSIMs),
 *     which lands with nothing but URL params — no plan, no items, no user. The
 *     purchase payload is therefore snapshotted before leaving and read back on
 *     return.
 *  2. The gap between "Buy now" and the order id. `tygoOrderId` is only minted
 *     by the checkout call at step 4 of 5, so begin_checkout mints a local
 *     `checkout_id` to stitch the early funnel and hands over once the real id
 *     exists.
 *
 * sessionStorage is deliberate: it is tab-scoped, so opening a confirmation URL
 * in a fresh tab finds no snapshot and cannot re-fire a purchase.
 */

import { TrackingItem } from "./dataLayer";
import { UserData } from "./hash";

const ACTIVE_KEY = "tygo_tracking_checkout";
const PENDING_PREFIX = "tygo_pending_purchase:";
const FIRED_PREFIX = "tygo_purchase_fired:";

// Safari private mode throws on write; tracking must never break the checkout.
const readJson = (store: Storage, key: string): any => {
  try {
    const raw = store.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJson = (store: Storage, key: string, value: any) => {
  try {
    store.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — tracking degrades, checkout continues */
  }
};

const remove = (store: Storage, key: string) => {
  try {
    store.removeItem(key);
  } catch {
    /* as above */
  }
};

export interface ActiveCheckout {
  checkout_id: string;
  item?: TrackingItem;
  user_data?: UserData;
}

export const startCheckout = (checkout: ActiveCheckout) => {
  writeJson(sessionStorage, ACTIVE_KEY, checkout);
};

export const getActiveCheckout = (): ActiveCheckout | null =>
  readJson(sessionStorage, ACTIVE_KEY);

/** Merge into the active checkout — used to attach hashed em/ph after OTP. */
export const updateActiveCheckout = (patch: Partial<ActiveCheckout>) => {
  const current = getActiveCheckout() || { checkout_id: "" };
  writeJson(sessionStorage, ACTIVE_KEY, { ...current, ...patch });
};

export const clearActiveCheckout = () => remove(sessionStorage, ACTIVE_KEY);

export interface PendingPurchase {
  transaction_id: string;
  checkout_id?: string;
  value: number;
  currency: string;
  payment_type?: string;
  items: TrackingItem[];
  user_data?: UserData;
}

export const savePendingPurchase = (purchase: PendingPurchase) => {
  if (!purchase.transaction_id) return;
  writeJson(sessionStorage, PENDING_PREFIX + purchase.transaction_id, purchase);
};

/** Reads and removes in one go, so a re-render cannot pick it up twice. */
export const takePendingPurchase = (
  transactionId?: string | null
): PendingPurchase | null => {
  if (!transactionId) return null;
  const key = PENDING_PREFIX + transactionId;
  const purchase = readJson(sessionStorage, key);
  if (purchase) remove(sessionStorage, key);
  return purchase;
};

/**
 * Survives a reload of the confirmation page, which re-runs the confirm call
 * and would otherwise push a second purchase for the same order. localStorage
 * rather than sessionStorage so it also holds across a restored tab.
 */
export const hasPurchaseFired = (transactionId: string): boolean => {
  try {
    return localStorage.getItem(FIRED_PREFIX + transactionId) !== null;
  } catch {
    return false;
  }
};

export const markPurchaseFired = (transactionId: string) => {
  try {
    localStorage.setItem(FIRED_PREFIX + transactionId, String(Date.now()));
  } catch {
    /* as above */
  }
};

/**
 * Once-per-order guard for order events other than the GA4 purchase, which
 * keeps its own marker above. Keyed by event name as well as order id, so a
 * new event can never suppress an existing one by firing first.
 */
const ORDER_EVENT_FIRED_PREFIX = "tygo_order_event_fired:";

const orderEventKey = (event: string, orderId: string) =>
  ORDER_EVENT_FIRED_PREFIX + event + ":" + orderId;

export const hasOrderEventFired = (event: string, orderId: string): boolean => {
  try {
    return localStorage.getItem(orderEventKey(event, orderId)) !== null;
  } catch {
    return false;
  }
};

export const markOrderEventFired = (event: string, orderId: string) => {
  try {
    localStorage.setItem(orderEventKey(event, orderId), String(Date.now()));
  } catch {
    /* as above */
  }
};
